import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useLessonStore } from '@/store/lessonStore'
import { apiClient } from '@/services/api'
import { LessonTimer } from './LessonTimer'
import { LessonDeadline } from './LessonDeadline'
import { PlayerManager } from './PlayerManager'
import { ActivityController } from './ActivityController'
import { LiveResponses } from './LiveResponses'
import { LessonAnalytics } from './LessonAnalytics'
import { LessonControls } from './LessonControls'
import { SelfPacedDashboard } from './SelfPacedDashboard'
import { webSocketService } from '@/services/websocket'
import { ArrowLeft, BarChart2, QrCode, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import type { Lesson } from '@/types'

export function LessonMaster() {
  const { lessonId } = useParams()
  const navigate = useNavigate()
  const {
    currentLesson,
    players,
    currentActivity,
    isConnected,
    isLoading,
    setCurrentLesson,
    connectToLesson,
    disconnectFromLesson,
    setLoading,
    setError,
    setPlayers,
    setCurrentActivity,
    setResponses
  } = useLessonStore()

  const [activeTab, setActiveTab] = useState<'overview' | 'players' | 'responses' | 'analytics' | 'progress'>('overview')

  const isSelfPaced = currentLesson?.mode === 'self_paced'

  const SLIDE_TYPES = ['pdf_slideshow', 'google_slides', 'youtube_video']
  const isCurrentActivitySlide = currentActivity ? SLIDE_TYPES.includes(currentActivity.activity_type) : false
  const showTimer = !isSelfPaced && !isCurrentActivitySlide
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [showQR, setShowQR] = useState(false)

  const joinUrl = currentLesson ? `${window.location.origin}/join/${currentLesson.run_code}` : ''

  const deadlineCompletedRef = useRef(false)
  const handleDeadlineExpire = useCallback(async () => {
    if (!currentLesson?.id || deadlineCompletedRef.current) return
    if (currentLesson.status === 'completed') return
    deadlineCompletedRef.current = true
    try {
      const response = await apiClient.completeLesson(currentLesson.id)
      if (response.success) {
        useLessonStore.getState().updateLessonState({ status: 'completed' })
      }
    } catch (error) {
      console.error('[LessonMaster] Failed to complete lesson on deadline expiry:', error)
      deadlineCompletedRef.current = false
    }
  }, [currentLesson?.id, currentLesson?.status])

  useEffect(() => {
    if (!lessonId) {
      navigate('/courses')
      return
    }

    // IMPORTANT: Reset all lesson state before loading new lesson
    setCurrentLesson(null)
    setPlayers([])
    setCurrentActivity(null)
    setError(null)
    disconnectFromLesson() // Disconnect from any previous lesson
    webSocketService.reinitialize() // Reinitialize WebSocket service

    loadLesson()

    // Set up polling for real-time updates since WebSocket isn't working reliably
    // Use useLessonStore.getState() to avoid stale closure over currentLesson (null at mount)
    const pollInterval = setInterval(() => {
      const lesson = useLessonStore.getState().currentLesson
      if (lesson?.id) {
        pollLessonUpdates()
      }
    }, 5000) // Poll every 5 seconds (stay well under the 100 req/min rate limit)

    return () => {
      disconnectFromLesson()
      clearInterval(pollInterval)
    }
  }, [lessonId]) // Remove currentLesson?.id dependency to avoid infinite loops

  const loadLesson = async () => {
    try {
      setLoading(true)

      // Get full lesson data with players using authenticated endpoint
      const lessonResponse = await apiClient.getLesson(lessonId!)

      if (!lessonResponse.success) {
        throw new Error(lessonResponse.message || 'Failed to load lesson details')
      }

      const lesson: Lesson = (lessonResponse.data as any).lesson || lessonResponse.data

      setCurrentLesson(lesson)

      // Set players from the full lesson data (always included in authenticated endpoint)
      if (lesson.players) {
        setPlayers(lesson.players)
      }

      // Activities are already included in the lesson response from authenticated endpoint
      // No need for separate activities call since DirectLessonsController includes them

      // Load responses for any active activity
      if (lesson.status === 'live' && lesson.activities) {
        const activeActivity = lesson.activities.find(a => a.status === 'active')
        if (activeActivity) {
          setCurrentActivity(activeActivity)

          try {
            const responsesResponse = await apiClient.getResponses(lesson.id, activeActivity.id)
            if (responsesResponse.success && responsesResponse.data) {
              setResponses(responsesResponse.data)
            }
          } catch (responseError) {
            // silently ignore
          }
        }
      }

      // Connect to real-time updates as host
      const unsubscribe = connectToLesson(lesson.id, 'host')

      // Store unsubscribe function for cleanup
      return unsubscribe
    } catch (error: any) {
      setError(error.message || 'Failed to load lesson')
      if (error.response?.status === 404) {
        navigate('/courses')
      }
    } finally {
      setLoading(false)
      setIsInitialLoad(false)
    }
  }

  const pollLessonUpdates = async () => {
    // Read directly from store to avoid stale closure
    const storeState = useLessonStore.getState()
    const lesson = storeState.currentLesson
    if (!lesson?.id) return

    try {
      // Get updated lesson state (includes players and activities)
      const lessonResponse = await apiClient.getLesson(lesson.id)
      if (lessonResponse.success && lessonResponse.data) {
        const updatedLesson = (lessonResponse.data as any).lesson || lessonResponse.data
        setCurrentLesson(updatedLesson)

        // Update players from the lesson data (players are included in the lesson response)
        if (updatedLesson.players) {
          setPlayers(updatedLesson.players)
        }

        // Update current activity if lesson is running
        if (updatedLesson.status === 'live' && updatedLesson.activities) {
          const activeActivity = updatedLesson.activities.find((a: any) => a.status === 'active' || a.status === 'paused')

          // Skip overwriting currentActivity if a recent extend/timer action is suppressing polls
          const suppressUntil = useLessonStore.getState()._suppressActivityPollUntil
          if (Date.now() < suppressUntil) {
            // Still update the lesson and players, but don't touch currentActivity
          } else {
            setCurrentActivity(activeActivity || null)
          }

          // Fetch responses for the active activity
          if (activeActivity && activeActivity.id) {
            try {
              const responsesResponse = await apiClient.getResponses(lesson.id, activeActivity.id)
              if (responsesResponse.success && responsesResponse.data) {
                setResponses(responsesResponse.data)
              } else {
                setResponses([]) // Clear old responses
              }
            } catch (responseError) {
              setResponses([]) // Clear old responses on error
            }
          } else {
            setResponses([])
          }
        }
      }
    } catch (error) {
      // Silently handle polling errors to avoid spam
    }
  }

  if (!currentLesson) {
    return (
      <div className="min-h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[var(--color-primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading lesson...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-theme-bg text-theme-text">
      {/* Header with lesson info and connection status */}
      <div className="border-b border-theme-border bg-chrome-bg" data-tour="lesson-master-header">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg bg-[var(--color-input-bg)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
              <h1 className="ds-page-title flex items-center gap-2">
                {currentLesson.name}
                {isLoading && !isInitialLoad && (
                  <div className="animate-spin w-3 h-3 border border-[var(--color-primary)] border-t-transparent rounded-full opacity-30"></div>
                )}
              </h1>
              <div className="flex items-center gap-4 mt-1">
                <span className="text-sm text-theme-text-muted">Lesson Code: {currentLesson.run_code}</span>
                <div className={`flex items-center gap-2 text-sm ${
                  currentLesson.status === 'completed' || currentLesson.status === 'cancelled'
                    ? 'text-[var(--color-text-muted)]'
                    : currentLesson.status === 'live'
                      ? 'text-green-400'
                      : 'text-yellow-400'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    currentLesson.status === 'completed' || currentLesson.status === 'cancelled'
                      ? 'bg-[var(--color-text-muted)]'
                      : currentLesson.status === 'live'
                        ? 'bg-green-400'
                        : 'bg-yellow-400'
                  }`}></div>
                  {currentLesson.status === 'live' ? 'In Progress' : currentLesson.status === 'completed' ? 'Completed' : currentLesson.status === 'cancelled' ? 'Cancelled' : 'Ready'}
                </div>
                {!isConnected && (
                  <span className="text-xs text-yellow-400">(offline)</span>
                )}
              </div>
            </div>
            </div>

            <div className="flex items-center gap-4">
              {(currentLesson.status === 'completed' || currentLesson.status === 'cancelled') && (
                <button
                  onClick={() => navigate(`/lessons/${currentLesson.id}/report`)}
                  className="ds-btn ds-btn-secondary ds-btn-sm"
                >
                  <BarChart2 className="w-4 h-4" />
                  Lesson Report
                </button>
              )}
              <div className="text-right">
                <div className="text-sm text-theme-text-muted">Students</div>
                <div className="text-xl font-bold flex items-center gap-2">
                  {isInitialLoad ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-[var(--color-primary)] border-t-transparent rounded-full"></div>
                      <span className="text-theme-text-muted">...</span>
                    </>
                  ) : (
                    players.length
                  )}
                </div>
              </div>
              <div data-tour="lesson-controls"><LessonControls /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Enrollment banner — only shown when lesson is live */}
      {(currentLesson.status === 'live' || currentLesson.status === 'running') && (
        <div className="border-b border-theme-border bg-surface">
          <div className="container mx-auto px-4">
            <button
              onClick={() => setShowQR(!showQR)}
              className="flex items-center gap-2 py-2 text-sm text-theme-text-muted hover:text-theme-text transition-colors w-full"
            >
              <QrCode className="w-4 h-4" />
              <span>{showQR ? 'Hide QR' : 'Show QR'}</span>
              {showQR ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
            </button>
            {showQR && (
              <div className="pb-4 flex items-start gap-6 flex-wrap">
                <div>
                  <p className="text-xs text-theme-muted mb-2">Students go to <span className="font-mono text-theme-text">/join</span> and enter:</p>
                  <div className="text-3xl font-mono font-bold text-green-400 tracking-widest">{currentLesson.run_code}</div>
                </div>
                <div className="bg-white p-2 rounded-lg">
                  <QRCodeSVG value={joinUrl} size={100} bgColor="#ffffff" fgColor="#000000" level="M" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="container mx-auto px-4 py-6">
        {currentLesson.status === 'completed' ? (
          /* Lesson completed — show summary */
          <div className="flex flex-col items-center justify-center py-16">
            <div className="bg-surface-deep rounded-2xl border border-theme-border p-10 max-w-md w-full text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center mx-auto mb-6">
                <BarChart2 className="w-8 h-8 text-[var(--color-primary)]" />
              </div>
              <h2 className="text-xl font-bold text-theme-text mb-2">Lesson Completed</h2>
              <p className="text-theme-text-muted text-sm mb-6">
                This lesson has ended. View the report for detailed analytics.
              </p>
              <button
                onClick={() => navigate(`/lessons/${currentLesson.id}/report`)}
                className="ds-btn ds-btn-primary mx-auto flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                View Report
              </button>
            </div>
          </div>
        ) : currentLesson.status !== 'live' && currentLesson.status !== 'running' ? (
          /* Pre-lesson — ready to start */
          <div className="flex flex-col items-center justify-center py-16">
            <div className="bg-surface-deep rounded-2xl border border-theme-border p-10 max-w-md w-full text-center mb-8" data-tour="start-lesson-btn">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <BarChart2 className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-[var(--color-primary)] mb-2">Ready to Start</h2>
              <p className="text-theme-text-muted text-sm mb-6">
                Start the lesson to generate a join code for your students.
              </p>
              <LessonControls variant="center" />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-6">
            {/* Left sidebar - Timer and controls */}
            <div className="col-span-3 space-y-6">
              {isSelfPaced ? (
                <>
                  <LessonDeadline lessonId={currentLesson.id} onExpire={handleDeadlineExpire} />
                  <div className="bg-surface-deep rounded-lg p-4 border border-theme-border">
                    <div className="flex items-center gap-2 text-[var(--color-primary)] text-sm font-medium mb-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      Self-Paced Mode
                    </div>
                    <p className="text-xs text-theme-text-muted">Students progress independently through activities at their own pace.</p>
                  </div>
                </>
              ) : (
                <>
                  {showTimer && <LessonTimer />}
                  <div data-tour="activity-controller"><ActivityController /></div>
                </>
              )}
            </div>

            {/* Main content area */}
            <div className="col-span-9">
              {/* Tab navigation */}
              <div className="flex border-b border-theme-border mb-6">
                {(isSelfPaced ? [
                  { key: 'overview', label: 'Overview' },
                  { key: 'players', label: isInitialLoad ? 'Students (...)' : `Students (${players.length})` },
                  { key: 'progress', label: 'Student Progress' },
                  { key: 'analytics', label: 'Analytics' }
                ] : [
                  { key: 'overview', label: 'Overview' },
                  { key: 'players', label: isInitialLoad ? 'Students (...)' : `Students (${players.length})` },
                  { key: 'responses', label: 'Live Responses' },
                  { key: 'analytics', label: 'Analytics' }
                ]).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as typeof activeTab)}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                        : 'border-transparent text-theme-text-muted hover:text-theme-text'
                    }`}
                    {...(tab.key === 'analytics' ? { 'data-tour': 'analytics-tab' } : {})}
                  >
                    {tab.label}
                  </button>
                ))}

                {/* Present button — opens presentation in new tab */}
                <button
                  onClick={() => window.open(`/present/${currentLesson.run_code}`, '_blank')}
                  className="ml-auto px-4 py-2 font-medium text-theme-text-muted hover:text-theme-text transition-colors flex items-center gap-1.5 border-b-2 border-transparent"
                  data-tour="present-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  Present
                </button>
              </div>

              {/* Tab content */}
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                {activeTab === 'overview' && (
                  <div className="grid grid-cols-2 gap-6">
                    <PlayerManager isLoading={isInitialLoad} />
                    <LessonAnalytics />
                  </div>
                )}

                {activeTab === 'players' && (
                  <PlayerManager expanded isLoading={isInitialLoad} />
                )}

                {activeTab === 'responses' && (
                  <LiveResponses />
                )}

                {activeTab === 'progress' && currentLesson && (
                  <SelfPacedDashboard lessonId={currentLesson.id} />
                )}

                {activeTab === 'analytics' && (
                  <LessonAnalytics expanded />
                )}
              </motion.div>
            </div>
          </div>
        )}
      </div>

      {/* Connection info overlay */}
      {!isConnected && (
        <div className="fixed bottom-4 right-4 bg-yellow-900 text-yellow-100 p-4 rounded-lg max-w-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
            <span className="font-medium">Basic Mode</span>
          </div>
          <p className="text-sm mt-1">
            Real-time updates disabled. Use refresh to see latest data.
          </p>
        </div>
      )}
    </div>
  )
}